import { z } from 'zod';
import {
  ProceduralActPhaseSchema,
  ProceduralActTypeSchema,
  ProceduralEnqueteKindSchema,
  getProceduralFactPhaseValidationIssues,
} from './procedural-act.js';
import {
  CategorieIntervenantSchema,
  FonctionActeurProcedureSchema,
  RolePartieProcesSchema,
} from './procedural-status.js';

/**
 * Case Wiki — entity schemas.
 *
 * Zod schemas (source of truth) + types derived via `z.infer`.
 * Aligned with migration 056_case_wiki_v4_schema.sql and DEBT-029 §2.
 *
 * Shared between the backend case-wiki service (ingest / lint / review BullMQ
 * workers + read APIs) and the frontend legalworkspace wiki UI.
 */

// ---------------------------------------------------------------------------
// Enums (mirror migration 056 enums 1:1)
// ---------------------------------------------------------------------------

/** Evidentiary lifecycle of a fact, from allegation to ruling. */
export const FactEvidentiaryStatusSchema = z.enum([
  'allegue',
  'etabli',
  'conteste',
  'rejete',
]);
export type FactEvidentiaryStatus = z.infer<typeof FactEvidentiaryStatusSchema>;

/** High-level classification of the fact. Drives UI grouping and pivot heuristics. */
export const FactNatureSchema = z.enum([
  'procedural',
  'material',
  'numeric',
  'corporate',
  'allegation',
]);
export type FactNature = z.infer<typeof FactNatureSchema>;

/** Procedural side of an entity (party or fact) in the dispute. */
export const EntitySideSchema = z.enum(['ours', 'adverse', 'neutral']);
export type EntitySide = z.infer<typeof EntitySideSchema>;

/**
 * Strategic role of a party in the dispute (orthogonal to `EntitySide`).
 *
 * - `EntitySide` (`ours/adverse/neutral`) reflects the documentary provenance
 *   of the piece where the party was first mentioned (cf. common-preamble.md
 *   "le champ side doit être cohérent avec la provenance de la pièce courante").
 * - `StrategicSide` (this) reflects the procedural role of the party at trial,
 *   independent of where they appear in the file. A witness produced by the
 *   opposing party is `side='adverse'` but `strategic_side='third_party'`.
 *
 * Filled by the Ingest agent which receives `client_name` + `procedure_type`
 * in its user prompt and arbitrates accordingly. Multiple parties may share
 * `our_client` (co-demandeurs, SAS+gérant, cohéritiers, époux solidaires…) —
 * no uniqueness constraint at the DB level.
 */
export const StrategicSideSchema = z.enum([
  'our_client',
  'opponent',
  'third_party',
  'unset',
]);
export type StrategicSide = z.infer<typeof StrategicSideSchema>;

/** Source document category for facts (mirrors DB enum `document_category`). */
export const DocumentCategorySchema = z.enum([
  'nos_pieces',
  'procedure',
  'procedures',
  'partie_adverse',
  'notes_projets',
  'correspondances',
  'recherche',
]);
export type DocumentCategory = z.infer<typeof DocumentCategorySchema>;

/** Party legal type (mirrors DB enum `party_type`). */
export const PartyTypeSchema = z.enum(['individual', 'corporation', 'institution']);
export type PartyType = z.infer<typeof PartyTypeSchema>;

/** Nature of the link between a fact and one of its pieces. */
export const FactPieceRelationSchema = z.enum([
  'source',
  'corroborates',
  'contradicts',
]);
export type FactPieceRelation = z.infer<typeof FactPieceRelationSchema>;

/** Which stage of the pipeline flagged a contradiction. */
export const ContradictionDetectedBySchema = z.enum([
  'ingest',
  'linter',
  'reviewer',
]);
export type ContradictionDetectedBy = z.infer<typeof ContradictionDetectedBySchema>;

/**
 * Type of generated wiki document (narrative pages assembled from facts).
 * Les types historiques `phases` / `arguments` / `principal_parties` ont été
 * retirés (nettoyage 2026-07-03, décision handoff case-wiki-virtual-fs §3.7) :
 * plus aucune passe ne les produit et plus aucune surface ne les lit.
 */
export const WikiDocumentTypeSchema = z.enum([
  'snapshot',
  'litigation_review',
  'nullities_admissibility',
]);
export type WikiDocumentType = z.infer<typeof WikiDocumentTypeSchema>;

/** Type of wiki run recorded in the runs table. */
export const WikiRunTypeSchema = z.enum(['ingest', 'lint', 'consolidation', 'review']);
export type WikiRunType = z.infer<typeof WikiRunTypeSchema>;

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

/** Exemple de slug cité dans les refus : un refus sans exemple fait retenter la même forme. */
const SLUG_EXAMPLE = 'fait-licenciement-2024-03-15';

/**
 * Ramène un slug écrit par un agent à sa forme kebab-case ASCII : minuscules, accents retirés,
 * toute suite d'autres caractères (espace, `_`, apostrophe, tirets répétés) remplacée par un seul
 * tiret, tirets de bord retirés. Une valeur qui n'est pas une chaîne est rendue telle quelle : la
 * validation la refuse.
 *
 * La transformation est idempotente et s'applique à la création comme à la référence, donc deux
 * écritures du même slug (`Fait-Réunion`, `fait-reunion`) désignent la même ligne.
 */
export function normalizeSlugInput(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    // NFD ne décompose pas les ligatures : sans cette ligne « cœur » deviendrait « c-ur ».
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const slugErrorMap: z.ZodErrorMap = (issue, ctx) => {
  const expected = `attendu 3 à 200 caractères, minuscules ASCII et chiffres séparés par des tirets, ex. « ${SLUG_EXAMPLE} »`;
  if (issue.code === z.ZodIssueCode.too_small) {
    // La valeur citée fait moins de 3 caractères : elle ne peut pas porter un nom de partie.
    return { message: `slug trop court : après normalisation il reste « ${String(ctx.data)} » ; ${expected}` };
  }
  if (issue.code === z.ZodIssueCode.too_big) {
    return { message: `slug trop long ; ${expected}` };
  }
  return { message: ctx.defaultError };
};

/** Slug kebab-case ASCII en minuscules, tel qu'il est stocké. Strict : sert aussi à contrôler un slug lu en base. */
export const SlugSchema = z
  .string()
  .min(3)
  .max(200)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug must be lowercase kebab-case ASCII');

/**
 * Slug ÉCRIT par un agent (création ou référence dans un outil).
 *
 * L'entrée est normalisée avant validation (`normalizeSlugInput`) : une majuscule, un accent ou un
 * double tiret ne valent pas un refus (281 refus en 7 jours de production, issue #2048), et un
 * `refine` aurait été invisible du modèle. `z.preprocess` laisse le schéma JSON montré à l'agent
 * identique à celui de `SlugSchema` (`pattern`, `minLength`, `maxLength`). Ne restent refusés que
 * la longueur et le type.
 *
 * Schéma distinct de `SlugSchema` parce que celui-ci contrôle aussi des slugs lus en base (worklist
 * du lint, bundle du frontend) : normaliser là changerait l'identité d'un slug stocké non canonique
 * au lieu de le signaler.
 */
export const SlugInputSchema = z.preprocess(
  normalizeSlugInput,
  z
    .string({ errorMap: slugErrorMap })
    .min(3)
    .max(200)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
) as z.ZodEffects<z.ZodString, string, string>;

/** Longueur au-delà de laquelle une date refusée est coupée dans le message de refus. */
const REJECTED_DATE_ECHO_MAX_CHARS = 40;

/**
 * Ramène à `AAAA-MM-JJ` les écritures d'une date complète qui n'ont qu'une lecture :
 * date ISO suivie d'une heure (`2024-03-15T10:30:00Z` → `2024-03-15`) et date française
 * `JJ/MM/AAAA` (séparateur `/`, `.` ou `-`). Tout le reste est rendu tel quel et refusé par le
 * schéma : année ou mois seuls (la règle des prompts est `null` + précision dans le texte, le
 * serveur n'invente pas un jour), date en toutes lettres, `MM/JJ/AAAA` reconnaissable (mois > 12).
 */
export function normalizeIsoDateInput(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  const withTime = /^(\d{4}-\d{2}-\d{2})[T ]\d{2}:\d{2}/.exec(trimmed);
  if (withTime) return withTime[1];
  const french = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(trimmed);
  if (french) {
    const day = Number(french[1]);
    const month = Number(french[2]);
    const year = Number(french[3]);
    // Un jour hors calendrier (31/02) reste refusé ici, avec la valeur reçue, plutôt que par PostgreSQL.
    const calendarDay = new Date(Date.UTC(year, month - 1, day));
    if (calendarDay.getUTCMonth() === month - 1 && calendarDay.getUTCDate() === day) {
      return `${french[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  return trimmed;
}

const isoDateErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code !== z.ZodIssueCode.invalid_string) return { message: ctx.defaultError };
  const received = String(ctx.data).slice(0, REJECTED_DATE_ECHO_MAX_CHARS);
  return {
    message:
      `date « ${received} » refusée : attendu AAAA-MM-JJ (ex. « 2024-03-15 »). ` +
      'Date incomplète (année ou mois seuls) ou incertaine : ne la complète pas, ' +
      'mets null ou omets le champ, et écris la précision dans le texte.',
  };
};

/** ISO 8601 date (YYYY-MM-DD). */
export const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be ISO 8601 (YYYY-MM-DD)');

/**
 * Date ÉCRITE par un agent. L'entrée passe par `normalizeIsoDateInput` avant validation ; le refus
 * restant cite la valeur reçue (252 refus muets en 7 jours de production, issue #2048). Le schéma
 * JSON montré est celui d'`IsoDateSchema`.
 */
export const IsoDateInputSchema = z.preprocess(
  normalizeIsoDateInput,
  z.string({ errorMap: isoDateErrorMap }).regex(/^\d{4}-\d{2}-\d{2}$/),
) as z.ZodEffects<z.ZodString, string, string>;

/** Quote length bounds — mirror DB CHECK `char_length(quote) BETWEEN 10 AND 2000`. */
export const QuoteSchema = z.string().min(10).max(2000);

/** Longueur au-delà de laquelle `deferred_items[].reason` est coupé. */
export const DEFERRED_ITEM_REASON_MAX_CHARS = 500;
/** Longueur au-delà de laquelle le champ `notes` d'un outil de fin est coupé. */
export const FINISH_NOTES_MAX_CHARS = 2000;
/** Longueur au-delà de laquelle la justification d'une fusion ou d'un renommage est coupée. */
export const TOOL_JUSTIFICATION_MAX_CHARS = 500;

/**
 * Texte libre INTERNE d'un outil d'agent (justification, note, motif de report) : le minimum est
 * exigé, le dépassement du maximum est coupé au lieu d'être refusé.
 *
 * Un modèle ne compte pas ses caractères. Avec `z.string().max(n)`, un motif de 520 caractères
 * faisait refuser l'appel entier — y compris un outil de fin, donc la passe (≈170 refus par
 * semaine en production, relevé du 2026-10-09). Ces textes ne sont jamais montrés à l'avocat :
 * perdre leur fin ne coûte rien, refuser l'appel coûte la passe.
 *
 * À ne PAS employer pour un texte affiché (résumé, titre, contenu de document) : le couper en
 * silence livrerait une phrase tronquée à l'avocat.
 *
 * Le schéma JSON montré au modèle ne porte plus de `maxLength` (une transformation est rendue par
 * son schéma d'entrée) : la borne se dit dans la description de l'outil.
 */
export function clippedText(min: number, max: number): z.ZodEffects<z.ZodString, string, string> {
  const base = min > 0 ? z.string().min(min) : z.string();
  return base.transform((value) => {
    if (value.length <= max) return value;
    // Une coupe au milieu d'une paire de substitution (émoji, caractère hors plan de base) laisse
    // une demi-séquence que PostgreSQL refuse dans une colonne jsonb.
    const lastUnit = value.charCodeAt(max - 1);
    const splitsSurrogatePair = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
    return value.slice(0, splitsSurrogatePair ? max - 1 : max);
  });
}

/**
 * Item de travail différé, signalé par un agent à la fin d'une passe quand il
 * a vu un problème qu'il ne peut/doit pas corriger ici. Remplace la prose libre
 * `notes` comme canal exploitable : structuré, carry-forward vers la phase
 * suivante, et porteur de la `reason` qu'exige l'échappatoire des gates P0
 * (ex. orphelin procédural résiduel justifié). Persisté en
 * `case_wiki_runs.payload.finish.deferred_items` (jsonb, zéro migration).
 *
 * Vit dans `entities.ts` (et NON `tool-payloads.ts`) pour rester un module
 * neutre : `lint-payloads.ts` et `review-payloads.ts` l'importent sans créer
 * d'import circulaire avec `tool-payloads.ts`. Le barrel (`index.ts:8`)
 * réexporte déjà tout `entities.js`, donc `DeferredItemSchema` / `DeferredItem`
 * sont publics sans ligne supplémentaire.
 */
export const DeferredItemSchema = z.object({
  kind: z.enum([
    'unattached_procedural_fact',
    'duplicate_party',
    'missing_link',
    'weak_description',
    'unresolved_ref',
    'other',
  ]),
  // nullish (pas optional) : Flash émet parfois `ref_slug: null` pour « je diffère
  // cet item sans slug précis » — sémantiquement identique à omis, mais `.optional()`
  // (= string | undefined) REFUSE `null` → Zod rejette le finish_review_pass → la
  // passe consolidation échoue en CASE_WIKI_CONSOLIDATION_NO_FINISH (masqué en
  // INTERNAL_ERROR), incident 2026-07-06. Même classe que le bug bordereau `.strict()`
  // (#83) : un schéma de sortie LLM trop rigide fait échouer un LLM qui a réussi.
  // Downstream tolère déjà null (extractJustifiedOrphanSlugs teste `typeof === 'string'`).
  ref_slug: SlugSchema.nullish(),
  // Coupé, pas refusé : un motif trop long ne doit pas faire échouer l'outil de fin.
  reason: clippedText(3, DEFERRED_ITEM_REASON_MAX_CHARS),
  severity: z.enum(['blocker', 'warning', 'info']).default('warning'),
});
export type DeferredItem = z.infer<typeof DeferredItemSchema>;

function withShape<TShape extends z.ZodRawShape, TSchema extends z.ZodTypeAny>(
  baseSchema: z.ZodObject<TShape>,
  schema: TSchema,
): TSchema & { shape: typeof baseSchema.shape } {
  return Object.assign(schema, { shape: baseSchema.shape });
}

function addStoredProceduralFactValidationIssues(
  value: {
    procedural_act_type?: string | null;
    procedural_phase?: string | null;
    enquete_kind?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  for (const issue of getProceduralFactPhaseValidationIssues(value)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [issue.path],
      message: issue.message,
    });
  }
}

// ---------------------------------------------------------------------------
// Facts
// ---------------------------------------------------------------------------

/**
 * Catégorie fonctionnelle v3 (#462) — axe « fonction de lecture » d'un fait,
 * orthogonal à `nature`/`side`. Remplace `pivot_score` (retiré en T6, cf. #462).
 */
export const FactCategorySchema = z.enum([
  'histoire',
  'procedure',
  'preuve',
  'contexte',
  'donnee_recurrente',
  'reference_externe',
]);
export type FactCategory = z.infer<typeof FactCategorySchema>;

/**
 * Thème sémantique par dossier (#462) — 8-12 thèmes semi-guidés,
 * resolve_or_create par la passe classification, stables entre re-runs.
 * Row de `case_fact_themes` (migration 135).
 */
export const FactThemeSchema = z.object({
  slug: SlugSchema,
  label: z.string().min(2).max(120),
  description: z.string().max(400).nullable().optional(),
  position: z.number().int().default(0),
});
export type FactTheme = z.infer<typeof FactThemeSchema>;

/**
 * Bornes de longueur des textes libres du wiki, en caractères. Source unique : les schémas d'écriture
 * (`tool-payloads.ts`, `lintRepairTools.ts`), les schémas de lecture ci-dessous et les descriptions
 * d'outils lues par l'agent s'y réfèrent, pour qu'un relèvement de plafond ne laisse pas un schéma en
 * retard qui rejetterait à la lecture ce que l'écriture a accepté.
 *
 * `FACT_SUMMARY_MAX_CHARS` doit rester égal au CHECK `case_facts_summary_check` (migration 199).
 * La description d'une partie n'a pas de CHECK en base : Zod est sa seule garde.
 *
 * Ces bornes s'appliquent à l'ÉCRITURE seulement. Les schémas de lecture (`FactBriefSchema`,
 * `PartySchema`) n'en portent aucune : le frontend parse le bundle entier d'un dossier avec eux, et
 * un onglet resté sur une version à l'ancien plafond rendait tout le dossier illisible dès qu'un
 * seul texte le dépassait. Une borne de lecture ne protège rien — la donnée est déjà en base.
 */
export const FACT_SUMMARY_MIN_CHARS = 50;
export const FACT_SUMMARY_MAX_CHARS = 2000;
export const PARTY_DESCRIPTION_MIN_CHARS = 20;
export const PARTY_DESCRIPTION_MAX_CHARS = 5000;

export const FactSummarySchema = z
  .string()
  .min(FACT_SUMMARY_MIN_CHARS)
  .max(FACT_SUMMARY_MAX_CHARS);
export const PartyDescriptionSchema = z
  .string()
  .min(PARTY_DESCRIPTION_MIN_CHARS)
  .max(PARTY_DESCRIPTION_MAX_CHARS);

/**
 * Compact fact record returned by `list_facts_brief`. Used by the reviewer
 * and linter agents to scan the full fact inventory cheaply.
 */
const FactBriefBaseSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(10).max(200),
  /** Sans borne en lecture : cf. `FACT_SUMMARY_MAX_CHARS`. */
  summary: z.string(),
  event_date: IsoDateSchema.nullable(),
  side: EntitySideSchema,
  nature: FactNatureSchema,
  evidentiary_status: FactEvidentiaryStatusSchema,
  /**
   * Catégorie fonctionnelle v3 (#462). NULL = wiki legacy pré-M135, ou fait
   * non encore classé par la passe classification.
   */
  category: FactCategorySchema.nullable().optional(),
  /**
   * Slug du thème (`case_fact_themes.slug`, référence souple same-case). NULL
   * = wiki legacy pré-M135, ou fait non encore classé.
   */
  theme_slug: SlugSchema.nullable().optional(),
  /**
   * Nullable/optional so `clean_procedure` can stream large brief reads while
   * old persisted bundles that predate procedure links remain parseable.
   */
  procedure_id: z.string().uuid().nullable().optional(),
  /**
   * Nullable/optional for penal procedure diagnostics: typed procedural facts
   * carry the act type, untyped procedural/material facts keep `null` or omit it.
   */
  procedural_act_type: ProceduralActTypeSchema.nullable().optional(),
  /**
   * Phase procédurale explicitement établie par la pièce source. Nullable pour
   * les faits legacy et pour les actes ambigus qui restent à consolider.
   */
  procedural_phase: ProceduralActPhaseSchema.nullable().optional(),
  /**
   * Sous-type d'enquête stocké uniquement quand la source établit qu'on est en
   * enquête et précise flagrance/préliminaire/sui generis. Null sinon.
   */
  enquete_kind: ProceduralEnqueteKindSchema.nullable().optional(),
});
export const FactBriefSchema = withShape(
  FactBriefBaseSchema,
  FactBriefBaseSchema.superRefine(addStoredProceduralFactValidationIssues),
);
export type FactBrief = z.infer<typeof FactBriefSchema>;

/** Piece cited as evidence for a fact (row in `case_fact_piece_sources`). */
export const FactPieceSourceSchema = z.object({
  /** `case_documents.wiki_slug` of the cited piece. */
  piece_slug: SlugSchema,
  relation: FactPieceRelationSchema,
  /** Verbatim quote — 10-2000 chars when relation is `source` or `contradicts`, nullable for `corroborates`. */
  quote: QuoteSchema.nullable(),
  quote_location: z.string().min(1).max(200).nullable(),
});
export type FactPieceSource = z.infer<typeof FactPieceSourceSchema>;

/** Party mentioned on a fact (row in `case_fact_parties`). */
export const FactPartyRefSchema = z.object({
  party_slug: SlugSchema,
  /** Per-fact role (e.g. "auteur", "victime", "témoin"). Nullable when absent. */
  role: z.string().min(2).max(120).nullable(),
});
export type FactPartyRef = z.infer<typeof FactPartyRefSchema>;

/**
 * Full fact entity as returned by `get_fact`. Hydrates the brief with the
 * source document category, strategic importance, procedure link, and the
 * linked parties/pieces.
 */
const FactSchemaBase = FactBriefBaseSchema.extend({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  source_document_category: DocumentCategorySchema,
  source_document_subcategory: z.string().min(1).max(120).nullable(),
  /** Piece slug that introduced the fact FIRST — scalar, not FK. Full relations in `pieces_sources`. */
  primary_source_piece_slug: SlugSchema,
  /**
   * Importance stratégique du fait dans le dossier — paragraphe 50-600 chars
   * rédigé par le Reviewer Pass 1 (cf. case-wiki/data-model.md §10.2).
   * Renommé depuis `pivot_rationale` (migration 068) — vocabulaire métier plus
   * clair. Nullable tant que le Reviewer n'a pas tourné sur ce fait.
   */
  strategic_importance: z.string().min(50).max(600).nullable(),
  /**
   * Procédure d'origine du fait (référé / fond / appel…). Nullable : pas tous
   * les faits sont attachés à une procédure (faits matériels par ex.). Lié via
   * FK `case_facts.procedure_id` — voir `procedure.ts`. Optionnel pour
   * compatibilité ascendante avec les bundles persistés avant M068.
   */
  procedure_id: z.string().uuid().nullable().optional(),
  tags: z.array(z.string().min(1).max(60)).default([]),
  /**
   * Type d'acte procédural pénal. Nullable : seuls les faits
   * `nature='procedural'` reconnus comme actes typés le portent. NULL = fait
   * procédural non typé, visible en Chronologie mais exclu du rail Procédure
   * curaté. Phase, lane et articles sont dérivés de `PROCEDURAL_ACT_INFO`.
   * `.optional()` pour compat ascendante avec les bundles persistés avant M118.
   */
  procedural_act_type: ProceduralActTypeSchema.nullable().optional(),
  parties: z.array(FactPartyRefSchema).default([]),
  pieces_sources: z.array(FactPieceSourceSchema).default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export const FactSchema = withShape(
  FactSchemaBase,
  FactSchemaBase.superRefine(addStoredProceduralFactValidationIssues),
);
export type Fact = z.infer<typeof FactSchema>;

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

/**
 * Compact party record returned by `list_parties_brief`.
 *
 * `title` min is intentionally 2 (not 10 like the other entities): short legal
 * forms such as "SC", "SA", "SNC", "GIE" are valid French party names. The
 * "noms affichables UI" rule from DEBT-029 §2.0 is about NOT-NULL presence,
 * not a hard min bound — enforcing `min(10)` here would reject legitimate
 * corporate names.
 */
export const PartyBriefSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(2).max(200),
  aliases: z.array(z.string().min(1).max(200)).default([]),
  party_type: PartyTypeSchema,
  side: EntitySideSchema,
  /**
   * Strategic role at trial (#459 filet consolidation — permet à la passe
   * `clean_participants` de repérer les candidats faux co-clients/adversaires
   * sans hydrater chaque partie via `get_party`). Cf. `StrategicSideSchema`.
   * Defaults to `'unset'`, jamais NULL en base (migration 065, NOT NULL DEFAULT).
   */
  strategic_side: StrategicSideSchema.default('unset'),
  /**
   * Civil/procedural quality (#459). Stocké en TEXT côté serveur (même
   * lecture relâchée que `PartySchema.civil_quality` — catalogue validé à
   * la frontière des tools par `CivilQualitySchema`, pas ici). Nullable tant
   * que l'agent ne l'a pas renseignée.
   */
  civil_quality: z.string().nullable().default(null),
  /**
   * Role of a true party at trial (MP, prévenu, partie civile...) (#459).
   * Nullable quand l'entité n'est pas une vraie partie au procès.
   */
  role_partie_proces: RolePartieProcesSchema.nullable().optional(),
});
export type PartyBrief = z.infer<typeof PartyBriefSchema>;

/** One procedural role a party holds in a given court (`case_parties.procedural_roles`). */
export const PartyProceduralRoleSchema = z.object({
  court: z.string().min(2).max(200),
  role: z.string().min(2).max(200),
});
export type PartyProceduralRole = z.infer<typeof PartyProceduralRoleSchema>;

/** Full party entity as returned by `get_party`. */
export const PartySchema = PartyBriefSchema.extend({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  /** Description de la partie : tronquée sur la carte de l'aperçu, entière sur sa fiche. */
  short_description: z.string().nullable(),
  procedural_roles: z.array(PartyProceduralRoleSchema).default([]),
  /**
   * Strategic role at trial — orthogonal to `side` (which reflects documentary
   * provenance). Defaults to `'unset'`. Cf. `StrategicSideSchema` and migration
   * `065_add_strategic_side_to_case_parties.sql`.
   */
  strategic_side: StrategicSideSchema.default('unset'),
  /**
   * Civil/procedural quality of the party, contextual to `cases.procedure_type`.
   * Catalogue in `civil-quality.ts` (closed enum, branch-aware). Stored as TEXT
   * server-side to absorb catalogue updates without a migration; validated at
   * the tool boundary by `CivilQualitySchema`.
   */
  civil_quality: z.string().nullable().default(null),
  /**
   * Penal V1 participant grouping. Optional for backward compatibility with
   * older payloads; new backend bundles hydrate it from `case_parties`.
   */
  categorie_intervenant: CategorieIntervenantSchema.optional(),
  /**
   * Role of a true party at trial (MP, prévenu, partie civile...). Nullable
   * when the entity is not a trial party or the role is still unknown.
   */
  role_partie_proces: RolePartieProcesSchema.nullable().optional(),
  /**
   * Procedural actor function (OPJ, juge d'instruction...). Nullable when the
   * entity is not a named procedural actor.
   */
  fonction_acteur_procedure: FonctionActeurProcedureSchema.nullable().optional(),
  /**
   * Pattern transverse — signal "édition manuelle prime sur agent". M068.
   * `'agent'` : valeur posée par l'agent. `'user'` : éditée manuellement par
   * un avocat (les ré-ingest ne doivent pas l'écraser). `'agent_validated'` :
   * agent puis validée explicitement par l'avocat (intermédiaire).
   */
  created_source: z.enum(['agent', 'user', 'agent_validated']).default('agent'),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type Party = z.infer<typeof PartySchema>;

// ---------------------------------------------------------------------------
// Contradictions & Questions
// ---------------------------------------------------------------------------

/**
 * Statut ternaire de résolution d'une contradiction (M068).
 * - `resolved` : tranchée (preuve écartée ou explicitée)
 * - `dismissed` : écartée (sans incidence stratégique)
 * - `noted` : à exploiter dans l'argumentaire
 */
export const ContradictionResolutionKindSchema = z.enum([
  'resolved', 'dismissed', 'noted',
]);
export type ContradictionResolutionKind = z.infer<typeof ContradictionResolutionKindSchema>;

/**
 * A contradiction between two facts, or between a fact and a piece.
 * `title` is mandatory (10-200 chars) — see DEBT-029 §2.0 "Noms affichables UI".
 * At least one of `fact_b_slug` / `piece_b_slug` must be set (enforced at the
 * tool layer via AppendContradictionPayloadSchema and by the DB CHECK).
 *
 * M068 ajoute `resolution_kind` (ternaire) et `resolved_by`. Le legacy
 * `resolved` boolean reste pour compat ascendante — à supprimer en v1.5.
 * Les colonnes M068 jamais peuplées (`severity`, `structural_type`,
 * `thematic_type`) ont été retirées du contrat (nettoyage 2026-07-03).
 */
export const ContradictionSchema = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  title: z.string().min(10).max(200),
  fact_a_slug: SlugSchema,
  fact_b_slug: SlugSchema.nullable(),
  piece_b_slug: SlugSchema.nullable(),
  explanation: z.string().min(30).max(2000),
  quote_a: z.string().min(10).max(2000).nullable(),
  quote_b: z.string().min(10).max(2000).nullable(),
  detected_by: ContradictionDetectedBySchema,
  /** Legacy boolean (M056) — préservé pour compat. Source de vérité = `resolution_kind`. */
  resolved: z.boolean().default(false),
  resolved_at: z.string().datetime().nullable(),
  resolved_note: z.string().min(10).max(1000).nullable(),
  /** M068 — statut ternaire. Nullable = pas encore traitée par l'avocat. */
  resolution_kind: ContradictionResolutionKindSchema.nullable().optional(),
  /** M068 — qui a résolu (user_id). */
  resolved_by: z.string().uuid().nullable().optional(),
  created_at: z.string().datetime(),
});
export type Contradiction = z.infer<typeof ContradictionSchema>;

/**
 * An open question raised during ingest/lint/review.
 * `title` is mandatory (10-200 chars) — see DEBT-029 §2.0 "Noms affichables UI".
 */
export const QuestionSchema = z.object({
  id: z.string().uuid(),
  case_id: z.string().uuid(),
  title: z.string().min(10).max(200),
  text: z.string().min(20).max(2000),
  raised_by_piece_slug: SlugSchema.nullable(),
  resolved: z.boolean().default(false),
  resolved_at: z.string().datetime().nullable(),
  resolved_explanation: z.string().min(10).max(1000).nullable(),
  created_at: z.string().datetime(),
});
export type Question = z.infer<typeof QuestionSchema>;
